<template>
  <div data-test="invite-restricted-page" class="restricted-invite-page">
    <div class="restricted-invite-container">
      <div class="logo-container">
        <img src="../../assets/brand/gyre-logo-full-pill.png" alt="Gyre logo" />
      </div>
      <div class="restricted-invites-message reason-container">
        <h2 data-test="invite-restricted-message">{{ errorTitle }}</h2>
        <h3 v-if="reasonForRestriction === InviteRestrictionReason.InvitesRestricted">
          Invites have been restricted
        </h3>
        <h3 v-if="reasonForRestriction === InviteRestrictionReason.NoAvailableLicences">
          No available licences
        </h3>
        <h3 v-if="reasonForRestriction === InviteRestrictionReason.AssessDeadlineExpired">
          The deadline for submitting your solo review has passed.
        </h3>
        <h3 v-if="reasonForRestriction === InviteRestrictionReason.InviteExpired">
          This invite has expired.
        </h3>
      </div>
      <div class="admin-contact-details">
        <h4>Please contact your inviter.</h4>
        <br />
        <hr class="green-underline" />
        <div class="btn-container">
          <a role="button" class="btn btn-lg btn-default" href="https://gyreteams.com/">Continue</a>
        </div>
      </div>
      <div class="error-image">
        <img src="../../assets/jyranians/jyranian-sad.png" alt="invite restriction" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { Analytics } from "../../services/analytics/analytics";
import { InviteRestrictionReason } from "../../typings/enums/invite-restriction-reason";
import { csContext } from "../../services/cs-context";

const route = useRoute();

const reasonForRestriction = computed(() => {
  switch (route.params.reason) {
    case InviteRestrictionReason.NoAvailableLicences:
    case InviteRestrictionReason.InvitesRestricted:
    case InviteRestrictionReason.AssessDeadlineExpired:
    case InviteRestrictionReason.InviteExpired:
      return route.params.reason;
    default:
      return "no reason given";
  }
});

const errorTitle = computed(() => {
  switch (reasonForRestriction.value) {
    case InviteRestrictionReason.InviteExpired:
      return "Invite Expired ⌛";
    case InviteRestrictionReason.AssessDeadlineExpired:
      return "Review Submission Closed ⌛";
    case InviteRestrictionReason.NoAvailableLicences:
      return "Sorry, you can't currently join this subscription.";
    default:
      return "Sorry, you can't currently join this team.";
  }
});

function logAssessDeadlineExpiredEvent(): void {
  Analytics.action("Review deadline passed", "GyreAssessActions", {
    userId: csContext.user.id,
    userName: `${csContext.user.firstName} ${csContext.user.lastName}`,
    userEmail: csContext.user.email,
    organisationId: csContext.user.organisation.id,
    organisationName: csContext.user.organisation.name,
  });
}

onMounted(() => {
  if (reasonForRestriction.value === InviteRestrictionReason.AssessDeadlineExpired) {
    logAssessDeadlineExpiredEvent();
  }
});
</script>

<style lang="less" scoped>
@import (reference) "../../styles/bootstrap/bootstrap.less";
@import (reference) "../../styles/bootstrap/variables.less";

.restricted-invite-page {
  width: 100%;
  height: 100vh;
  color: @dark-background-color;
  display: flex;
  justify-content: center;
}

.restricted-invite-container {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 40%;
  height: 100%;

  @media (max-width: 900px) {
    width: 75%;
    margin-top: 2rem;
  }
}

.logo-container {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;

  img {
    width: 33%;
  }
}

.reason-container {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  text-align: left;

  h2 {
    margin-bottom: 0;
  }

  @media (max-width: 440px) {
    h2,
    h3 {
      font-size: 1.5em;
    }
  }
}

.admin-contact-details {
  width: 100%;

  h4 {
    font-weight: normal;
    margin-bottom: 0;
    @media (max-width: 440px) {
      font-size: 1.5em;
    }
  }

  p {
    margin-bottom: 0;
  }
}

.admin-email {
  color: @brand-primary;
}

.btn-container {
  width: 100%;
  display: flex;
  justify-content: center;
}

.green-underline {
  border-top: 2px solid @charismatic-color;
  margin-top: 5px;
  margin-left: 0;
  width: 100%;
}

.error-image {
  display: flex;
  justify-content: center;
  width: 100%;

  img {
    width: 70%;
    overflow: hidden;

    @media (max-width: 820px) {
      width: 100%;
      margin-left: 0;
    }
  }
}
</style>
